import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiAgentBundleId } from '@euler/model/ai-home';
import {
  AiAttachmentInfo,
  ApdmBuildChecklist,
  ApdmBuildStep,
  ApdmCampaignMonitor,
  ApdmPlanSettledFacts,
} from '@euler/model/apdm';
import { parseBiUploadWidgetPayload } from '@euler/model/bi/widgets';
import { StatusBar } from 'expo-status-bar';
import { ChevronLeft, ChevronRight, LucideIcon } from 'lucide-react-native';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { NEVER, Observable } from 'rxjs';
import {
  AiChatMessage,
  AiChatReaction,
  AiChatState,
  AiChatStep,
  AiChatTap,
} from '../conversation';
import { AiSuiteTool, aiColors, aiSuiteToolForBundle, tint } from '../theme';
import { AiBuildSummary } from './AiBuildSummary';
import { AiCampaignMonitor, CampaignStatusDot } from './AiCampaignMonitor';
import {
  AiChatActions,
  AiChatActionsContext,
  AiChatCampaignContext,
  AiChatPlanContext,
  AiChatSettledContext,
} from './AiChatActions';
import { AiChatComposer, AiChatComposerHandle } from './AiChatComposer';
import { AssistantMessage, UserMessage } from './AiChatMessage';
import { replyParts, replySteps } from './AiReplyContent';

const kNearBottom = 120;
const kNoSteps: readonly ApdmBuildStep[] = [];
const kNoUploads: readonly string[] = [];
const kNoWork: readonly AiChatStep[] = [];

/** What each message holds, from what holds each thing. */
function byHolder<T>(holders: Map<T, string>): Map<string, T[]> {
  const byMessage = new Map<string, T[]>();
  for (const [value, key] of holders)
    byMessage.set(key, [...(byMessage.get(key) ?? []), value]);
  return byMessage;
}

export const AiChatView = memo(
  ({
    state,
    tool,
    showBack,
    backIcon: BackIcon = ChevronLeft,
    onBack,
    onSend,
    onStop,
    onRetry,
    onReact,
    onEarlier,
    checklist,
    settled,
    campaign,
    tapSettled$ = NEVER,
  }: {
    state: AiChatState;
    /** The tool the chat is focused on, for its chip and empty state. */
    tool: AiSuiteTool | null;
    showBack: boolean;
    /** A side panel closes rather than goes back. */
    backIcon?: LucideIcon;
    onBack: () => void;
    onSend: (
      text: string,
      attachments: AiAttachmentInfo[],
      tap?: AiChatTap,
    ) => boolean;
    onStop: () => void;
    onRetry: () => void;
    onReact: (key: string, reaction: AiChatReaction) => void;
    onEarlier: () => void;
    /** The conversation's open campaign build, for its cards. */
    checklist: ApdmBuildChecklist | null;
    /** What that build has settled, for the header's chips. */
    settled: ApdmPlanSettledFacts | null;
    /** The campaign it launched, live, for the header's monitor. */
    campaign: ApdmCampaignMonitor | null;
    /** The conversation's settled taps, for the cards that sent them. */
    tapSettled$?: Observable<AiChatTap>;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const styles = useStyles();
    const list = useRef<FlatList<AiChatMessage>>(null);
    const composer = useRef<AiChatComposerHandle>(null);
    const nearBottom = useRef(true);
    const lastOffset = useRef(0);
    const [width, setWidth] = useState(0);
    const title =
      state.messages.find(message => message.role === 'user')?.content ||
      (tool ? tool.title : '新对话');
    const lastAssistant = useMemo(() => {
      for (let index = state.messages.length - 1; index >= 0; index--)
        if (state.messages[index].role === 'assistant')
          return state.messages[index].key;
      return null;
    }, [state.messages]);
    // Per step, the reply holding its newest card: that card takes the
    // step's answer even after a side question.
    // Per file upload, likewise the reply holding its newest card: the
    // server writes the card again after each tap, and only the newest acts.
    const { answers, uploads } = useMemo(() => {
      const steps = new Map<ApdmBuildStep, string>();
      const files = new Map<string, string>();
      for (const message of state.messages)
        if (message.role === 'assistant') {
          const parts = replyParts(message.content);
          for (const step of replySteps(parts)) steps.set(step, message.key);
          for (const segment of parts.segments) {
            const ref =
              segment.kind === 'widget' && segment.widget === 'bi_upload'
                ? parseBiUploadWidgetPayload(segment.data)
                : null;
            if (ref) files.set(ref.uploadId, message.key);
          }
        }
      return { answers: byHolder(steps), uploads: byHolder(files) };
    }, [state.messages]);
    const sendText = useCallback((text: string) => onSend(text, []), [onSend]);
    const sendFromCard = useCallback<AiChatActions['send']>(
      (text, extra) =>
        onSend(
          text,
          extra?.attachments ?? [],
          extra?.answer
            ? { kind: 'apdm', answer: extra.answer }
            : extra?.biAnswer
            ? { kind: 'bi', answer: extra.biAnswer }
            : undefined,
        ),
      [onSend],
    );
    const actions = useMemo<AiChatActions>(
      () => ({
        send: sendFromCard,
        busy: state.busy,
        // After the press: on web the pressed button keeps the focus the
        // click gave it, and the composer would lose it at once.
        focusComposer: () => {
          setTimeout(() => composer.current?.focus(), 0);
        },
        tapSettled$,
      }),
      [sendFromCard, state.busy, tapSettled$],
    );
    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      // A card that loads its data grows the list under a scroll still on
      // its way to the end; only the reader scrolling up leaves the end.
      nearBottom.current =
        contentSize.height - contentOffset.y - layoutMeasurement.height <
          kNearBottom ||
        (nearBottom.current && contentOffset.y >= lastOffset.current);
      lastOffset.current = contentOffset.y;
      if (contentOffset.y < 40) onEarlier();
    };
    const renderItem = useCallback(
      ({ item }: { item: AiChatMessage }) =>
        item.role === 'user' ? (
          <UserMessage message={item} />
        ) : (
          <AssistantMessage
            message={item}
            latest={item.key === lastAssistant}
            answers={answers.get(item.key) ?? kNoSteps}
            biUploads={uploads.get(item.key) ?? kNoUploads}
            working={state.working}
            work={item.key === lastAssistant ? state.steps : kNoWork}
            width={Math.max(0, width - rpx(32))}
            onRetry={onRetry}
            onReact={onReact}
            onSuggestion={sendText}
          />
        ),
      [
        lastAssistant,
        answers,
        uploads,
        state.working,
        state.steps,
        width,
        rpx,
        onRetry,
        onReact,
        sendText,
      ],
    );
    // A conversation that built a campaign is a 私域营销 one, even when it
    // started on the home page: its chip carries the campaign's state.
    const chipTool =
      tool ??
      (settled || campaign ? aiSuiteToolForBundle(AiAgentBundleId.Apdm) : null);
    // The chip would only repeat a title that already names the tool.
    const ToolIcon =
      chipTool && (campaign || title !== chipTool.title)
        ? chipTool.icon
        : undefined;
    // An empty chat opens on the tool's welcome, set on the page background.
    const welcome = !state.loading && !state.messages.length && !!tool?.intro;
    return (
      <AiChatActionsContext.Provider value={actions}>
        <AiChatPlanContext.Provider value={checklist}>
          <AiChatSettledContext.Provider value={settled}>
            <AiChatCampaignContext.Provider value={campaign}>
              <KeyboardAvoidingView
                style={[
                  styles.page,
                  welcome && { backgroundColor: aiColors.background },
                ]}
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              >
                <StatusBar style="dark" />
                <View style={[styles.header, { paddingTop: insets.top }]}>
                  <View style={styles.headerRow}>
                    {showBack ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={
                          BackIcon === ChevronLeft ? '返回' : '关闭'
                        }
                        onPress={onBack}
                        style={styles.headerButton}
                      >
                        <BackIcon size={22} color={aiColors.foreground} />
                      </Pressable>
                    ) : (
                      <View style={{ width: rpx(8) }} />
                    )}
                    <View style={styles.headerTitle}>
                      <Typography.Text
                        bold
                        size={rpx(16)}
                        color={aiColors.foreground}
                        numberOfLines={1}
                      >
                        {title}
                      </Typography.Text>
                      {(chipTool && ToolIcon) || settled ? (
                        <View style={styles.chips}>
                          {chipTool && ToolIcon && (
                            <View
                              style={[
                                styles.toolChip,
                                { backgroundColor: tint(chipTool.color, 0.1) },
                              ]}
                            >
                              {campaign ? (
                                <CampaignStatusDot status={campaign.status} />
                              ) : null}
                              <ToolIcon size={11} color={chipTool.color} />
                              <Typography.Text
                                size={rpx(10)}
                                color={chipTool.color}
                              >
                                {chipTool.title}
                              </Typography.Text>
                            </View>
                          )}
                          {settled ? (
                            <AiBuildSummary
                              settled={
                                // Once launched the monitor counts the reach.
                                campaign
                                  ? { ...settled, audienceCount: null }
                                  : settled
                              }
                            />
                          ) : null}
                        </View>
                      ) : null}
                    </View>
                  </View>
                  {campaign ? (
                    <View style={styles.monitor}>
                      <AiCampaignMonitor campaign={campaign} />
                    </View>
                  ) : null}
                </View>
                <FlatList
                  ref={list}
                  data={state.messages}
                  keyExtractor={item => item.key}
                  renderItem={renderItem}
                  onLayout={(event: LayoutChangeEvent) =>
                    setWidth(event.nativeEvent.layout.width)
                  }
                  onScroll={onScroll}
                  scrollEventThrottle={64}
                  onContentSizeChange={() => {
                    // The scroll view aims at its content height; the
                    // list's own scrollToEnd aims at the last item's
                    // measured frame, which lags a card that just grew.
                    if (!nearBottom.current) return;
                    const scroller = list.current?.getNativeScrollRef() as
                      | ScrollView
                      | null
                      | undefined;
                    scroller?.scrollToEnd();
                  }}
                  keyboardShouldPersistTaps="handled"
                  keyboardDismissMode="interactive"
                  contentContainerStyle={styles.list}
                  ItemSeparatorComponent={Separator}
                  ListHeaderComponent={
                    state.loading ? (
                      <ActivityIndicator
                        color={aiColors.primary}
                        style={{ marginBottom: rpx(12) }}
                      />
                    ) : null
                  }
                  ListEmptyComponent={
                    state.loading ? null : (
                      <EmptyChat
                        tool={tool}
                        error={state.loadError}
                        onStarter={sendText}
                      />
                    )
                  }
                />
                <AiChatComposer
                  ref={composer}
                  busy={state.busy}
                  disabled={state.loading || state.loadError != null}
                  placeholder={
                    campaign
                      ? '问问活动进展、回复和线索…'
                      : chipTool?.bundleId && chipTool.placeholder
                      ? chipTool.placeholder
                      : '有什么想做的，告诉我…'
                  }
                  onSend={onSend}
                  onStop={onStop}
                />
              </KeyboardAvoidingView>
            </AiChatCampaignContext.Provider>
          </AiChatSettledContext.Provider>
        </AiChatPlanContext.Provider>
      </AiChatActionsContext.Provider>
    );
  },
);

const Separator = () => {
  const { rpx } = useResponsiveContext();
  return <View style={{ height: rpx(20) }} />;
};

const EmptyChat = memo(
  ({
    tool,
    error,
    onStarter,
  }: {
    tool: AiSuiteTool | null;
    error: string | null;
    onStarter: (text: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (error)
      return (
        <Typography.Text
          size={rpx(13)}
          color={aiColors.destructive}
          style={{ textAlign: 'center', marginTop: rpx(40) }}
        >
          {error}
        </Typography.Text>
      );
    if (!tool?.intro) return null;
    const Icon = tool.icon;
    return (
      <View style={{ paddingTop: rpx(16) }}>
        <View
          style={{
            width: rpx(56),
            height: rpx(56),
            borderRadius: rpx(16),
            backgroundColor: tool.color,
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: tool.color,
            shadowOpacity: 0.25,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
            elevation: 3,
          }}
        >
          <Icon size={26} color="#fff" fill="#fff" />
        </View>
        <Typography.Text
          bold
          size={rpx(20)}
          color={aiColors.foreground}
          style={{ marginTop: rpx(20) }}
        >
          {tool.heading}
        </Typography.Text>
        <Typography.Text
          size={rpx(14)}
          lineHeight={rpx(23)}
          color={aiColors.mutedForeground}
          style={{ marginTop: rpx(8) }}
        >
          {tool.intro}
        </Typography.Text>
        <Typography.Text
          medium
          size={rpx(12)}
          color={aiColors.mutedForeground}
          style={{ marginTop: rpx(28), marginBottom: rpx(10) }}
        >
          试试这样说
        </Typography.Text>
        <View style={{ gap: rpx(10) }}>
          {tool.starters.map(starter => {
            const StarterIcon = starter.icon;
            return (
              <Pressable
                key={starter.text}
                accessibilityRole="button"
                onPress={() => onStarter(starter.text)}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(12),
                  paddingHorizontal: rpx(14),
                  paddingVertical: rpx(14),
                  borderRadius: rpx(16),
                  borderWidth: 1,
                  borderColor: aiColors.border,
                  backgroundColor: aiColors.card,
                  shadowColor: '#0F172A',
                  shadowOpacity: 0.06,
                  shadowRadius: 6,
                  shadowOffset: { width: 0, height: 2 },
                  elevation: 2,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <View
                  style={{
                    width: rpx(32),
                    height: rpx(32),
                    borderRadius: rpx(10),
                    backgroundColor: tint(starter.color, 0.1),
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <StarterIcon size={16} color={starter.color} />
                </View>
                <Typography.Text
                  size={rpx(14)}
                  color={aiColors.foreground}
                  style={{ flex: 1 }}
                >
                  {starter.text}
                </Typography.Text>
                <ChevronRight size={16} color={aiColors.mutedForeground} />
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: { flex: 1, backgroundColor: aiColors.card },
        header: {
          backgroundColor: aiColors.card,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: aiColors.border,
        },
        headerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          minHeight: 56,
          paddingHorizontal: rpx(8),
        },
        headerButton: {
          width: 40,
          height: 40,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
        },
        headerTitle: { flex: 1, gap: rpx(3), alignItems: 'flex-start' },
        // As wide as the conversation column, like the composer.
        monitor: {
          width: '100%',
          maxWidth: rpx(760),
          alignSelf: 'center',
          paddingHorizontal: rpx(12),
          paddingBottom: rpx(12),
        },
        chips: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: rpx(6),
        },
        toolChip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(2),
          borderRadius: rpx(10),
        },
        list: {
          width: '100%',
          maxWidth: rpx(760),
          alignSelf: 'center',
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(16),
          flexGrow: 1,
        },
      }),
    [rpx],
  );
}
